import { useFirebaseAuth } from "@/hooks/use-firebase-auth";
import { Loader2 } from "lucide-react";
import { Redirect, Route } from "wouter";
import { UserRole } from "@/constants/roles";
import { normalizeRoles } from "@/lib/normalize-user-data";

export function AdminRoute({
  path,
  component: Component,
}: {
  path: string;
  component: () => React.JSX.Element;
}) {
  const { user, isLoading } = useFirebaseAuth();

  if (isLoading) {
    return (
      <Route path={path}>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-border" />
        </div>
      </Route>
    );
  }

  if (!user) {
    return (
      <Route path={path}>
        <Redirect to="/auth" />
      </Route>
    );
  }

  const allowedRoles = UserRole.ADMIN;
  // Check for roles in multiple possible locations
  const userRoles = user?.profile?.roles || user?.profile?.role || user?.roles || user?.role || [];
  
  // Handle both array and string role formats
  const rolesArray = Array.isArray(userRoles) ? userRoles : [userRoles].filter(Boolean);
  
  // Check if user has admin role (case insensitive)
  const hasAdminRole = rolesArray.some(role => 
    typeof role === 'string' && role.toLowerCase().includes('admin')
  );
  
  if (!hasAdminRole) {
    return (
      <Route path={path}>
        <Redirect to="/" />
      </Route>
    );
  }

  return <Route path={path} component={Component} />;
}

/**
 * Route component that restricts access to instructors only
 */
export function InstructorRoute({
  path,
  component: Component,
}: {
  path: string;
  component: () => React.JSX.Element;
}) {
  const { user, isLoading } = useFirebaseAuth();

  if (isLoading) {
    return (
      <Route path={path}>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-border" />
        </div>
      </Route>
    );
  }

  if (!user) {
    return (
      <Route path={path}>
        <Redirect to="/auth" />
      </Route>
    );
  }

  const roles = normalizeRoles(user);

  const hasInstructorRole = roles.some(
    (role) => role === "INSTRUCTOR" || role === "ADMIN"
  );

  if (!hasInstructorRole) {
    return (
      <Route path={path}>
        <Redirect to="/dashboard" />
      </Route>
    );
  }

  return <Route path={path} component={Component} />;
}
